import { RefObject, useCallback, useEffect, useRef } from 'react';

import { getCheckItemButtonSelector } from 'Src/feature/check-item/CheckItemButton';
import { useResizeObserver } from 'Src/feature/resize/useResizeObserver';
import useRoute from 'Src/routes/useRoute';

type OptionalRefObject = RefObject<HTMLElement> | RefObject<null> | RefObject<undefined>;

type UseCheckItemAutoScrollOptions = {
  scrollElementRef: OptionalRefObject;
  scrollContentRef: OptionalRefObject;
  checkItemUUIDs: string[];
  isOff: boolean;
}

/**
 * handle auto scrolling to the most recent check item
 */
const useCheckItemAutoScroll = (options: UseCheckItemAutoScrollOptions) => {
  const { route } = useRoute();

  const checkUUID = useRef({
    next: undefined as undefined | string,
    last: undefined as undefined | string,
    active: undefined as undefined | string,
  });

  const routedCheckItemUUID = options.checkItemUUIDs.find((uuid) => uuid === route.params.active.checkItemId);
  if (routedCheckItemUUID) {
    // get the active check item from the route params
    checkUUID.current.next = routedCheckItemUUID;
  } else {
    // or pull the last one in the collection (in the case of a new check item, it will be the last one
    const lastCheckItemUUID = options.checkItemUUIDs[options.checkItemUUIDs.length - 1];
    if (lastCheckItemUUID) {
      checkUUID.current.next = lastCheckItemUUID;
    }
  }

  const checkScroll = useCallback(() => {
    if (options.isOff) return;
    if (!checkUUID.current.next) return;

    // get the active check item button in the check item panel
    const btn = options.scrollElementRef.current?.querySelector(getCheckItemButtonSelector(checkUUID.current.next)) as (HTMLElement | undefined);
    const container = options.scrollElementRef.current as (HTMLElement | undefined);
    if (!btn || !container) return;
    const btnRect = btn?.getBoundingClientRect();
    const containerRect = container?.getBoundingClientRect();
    if (!btnRect || !containerRect) return;

    // if button is completely visible no need to scroll
    // - button element is **not** overflowing from the scroll container
    // - compare bottom of button to bottom of container (both in viewport coordinates)
    // - include a small buffer for borders/margin
    const isVisible = (btnRect.top >= containerRect.top) && (btnRect.bottom <= (containerRect.bottom + 5));

    if (isVisible) {
      // if visible, we update "last" so we don't try to scroll again unnecessarily
      checkUUID.current.last = checkUUID.current.next;
      return;
    }

    // we have already scrolled to the last
    if (checkUUID.current.last && checkUUID.current.next === checkUUID.current.last) return;

    // last has not yet scrolled so scroll
    checkUUID.current.last = checkUUID.current.next;

    // scroll to the last element every time the container resizes
    // - resize will happen when a new CheckItem is added
    // - resize will happen when CheckItem is moved to new hot keys sections
    options.scrollElementRef.current?.scrollTo({
      // add 20 pixels to move below the sticky group labels for "UseHotKeys"
      top: (btnRect.top - containerRect.top + container.scrollTop) - 20,
      behavior: 'smooth'
    });
  }, [options.isOff, options.scrollElementRef]);

  useResizeObserver({
    elementRef: options.scrollContentRef,
    onResize: checkScroll,
  });

  // use a ref to track the previous list of UUIDs to detect actual order changes
  // (the prop array reference might change on every render but contents might be same)
  const prevCheckItemUUIDs = useRef<string[]>(([]));

  // check scroll when the list of items changes (reorder) or the target item changes
  useEffect(() => {
    // simple equality check: active item changed OR list length changed OR list order changed
    const activeChanged = checkUUID.current.next !== checkUUID.current.last;

    const prev = prevCheckItemUUIDs.current;
    const curr = options.checkItemUUIDs;
    const listChanged = prev.length !== curr.length ||
      prev.some((uuid, i) => uuid !== curr[i]);

    if (listChanged) {
      // update the ref
      prevCheckItemUUIDs.current = curr;
      // if the list order changed, we MUST invalidate the "last" memory
      // because even if the active item ID is the same, its position has likely changed.
      checkUUID.current.last = undefined;
    }

    if (activeChanged || listChanged) {
      checkScroll();
    }
  }, [checkScroll, options.checkItemUUIDs, checkUUID.current.next]);
};

export { useCheckItemAutoScroll };
